import { faPlus, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, NotificationDispatch, type SelectOption, TextField } from '@tryhackme/thm-ui-components';
/* eslint-disable unicorn/no-useless-undefined */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import type { RoomBlueprint } from 'src/common/interfaces/room-blueprint';
import { StyledEmptyDataLabel, StyledFormGroup, StyledHeader } from '../categorization.styles';
import { StyledForm, StyledRow } from '../components/rule/rule.styles';
import { blueprintInitialValue, roomBlueprintValidation, stepInitialValue } from './blueprint.constants';
import { useGetBlueprintQuery, useGetBlueprintRulesQuery, useGetBlueprintTasksQuery, useSaveBlueprintMutation } from './blueprint.slice';
import { StyledContainer, StyledLoadingSpinner } from './blueprint.styles';
import type { BlueprintProps } from './blueprint.types';
import { BlueprintStep } from './components/step/step';
export const Blueprint = ({
  rooms
}: BlueprintProps) => {
  const [roomId, setRoomId] = useState<string | undefined>();
  const [ruleId, setRuleId] = useState<string | undefined>();
  const {
    data: rules,
    isLoading: isLoadingRules
  } = useGetBlueprintRulesQuery(roomId!, {
    skip: !roomId
  });
  const {
    data: tasks,
    isLoading: isLoadingTasks
  } = useGetBlueprintTasksQuery(roomId!, {
    skip: !roomId
  });
  const {
    data: blueprint
  } = useGetBlueprintQuery(roomId!, {
    skip: !roomId
  });
  const [saveBlueprint, {
    isLoading: isSaving
  }] = useSaveBlueprintMutation();
  const isDisabled = isLoadingRules || isLoadingTasks || isSaving;
  const methods = useForm<RoomBlueprint>({
    resolver: yupResolver(roomBlueprintValidation),
    values: blueprint ?? blueprintInitialValue(roomId),
    mode: 'all'
  });
  const {
    control,
    reset,
    handleSubmit
  } = methods;
  const onSubmit = async (payload: RoomBlueprint) => {
    if (!roomId) {
      return;
    }
    try {
      await saveBlueprint({
        roomId,
        payload
      }).unwrap();
      reset(payload);
      NotificationDispatch('success', 'Blueprint successfully saved');
    } catch (error) {
      if (isQueryErrorWithMessage(error)) {
        NotificationDispatch('error', error?.data?.message);
      } else {
        // eslint-disable-next-line no-console
        console.error(error);
        NotificationDispatch('error', 'Something went wrong');
      }
    }
  };
  const {
    fields,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'steps'
  });
  const handleRoomChange = (option: SelectOption) => {
    setRoomId(option.value);
    setRuleId(undefined);
  };
  const handleAddStep = () => {
    if (!ruleId) {
      return;
    }
    append(stepInitialValue(ruleId));
    setRuleId(undefined);
  };
  const handleRemoveStep = (index: number) => remove(index);
  const selectedRoom = useMemo(() => rooms.find(option => option.value === roomId), [roomId, rooms]);
  const ruleOptions = useMemo(() => rules?.map(rule => ({
    label: rule.title,
    value: rule._id
  })) ?? [], [rules]);
  const selectedRule = useMemo(() => ruleOptions.find(option => option.value === ruleId), [ruleId, ruleOptions]);
  const taskOptions = useMemo(() => tasks?.map(rule => ({
    label: rule.title,
    value: rule._id
  })) ?? [], [tasks]);
  return <StyledContainer data-sentry-element="StyledContainer" data-sentry-component="Blueprint" data-sentry-source-file="blueprint.tsx">
      <StyledHeader data-sentry-element="StyledHeader" data-sentry-source-file="blueprint.tsx">
        <StyledForm data-sentry-element="StyledForm" data-sentry-source-file="blueprint.tsx">
          <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="blueprint.tsx">
            <StyledFormGroup data-sentry-element="StyledFormGroup" data-sentry-source-file="blueprint.tsx">
              <TextField label="Room:" labelPosition="left" placeholder="Select room" variant="select" options={rooms} selected={selectedRoom} isSearchable isMulti={false} isDisabled={isDisabled} handleSelection={handleRoomChange} data-sentry-element="TextField" data-sentry-source-file="blueprint.tsx" />
            </StyledFormGroup>
          </StyledRow>

          <StyledRow data-sentry-element="StyledRow" data-sentry-source-file="blueprint.tsx">
            <StyledFormGroup data-sentry-element="StyledFormGroup" data-sentry-source-file="blueprint.tsx">
              <TextField label="Rule:" labelPosition="left" placeholder="Select rule" variant="select" isSearchable options={ruleOptions} selected={selectedRule} isMulti={false} isDisabled={isDisabled || !roomId} handleSelection={option => setRuleId(option.value)} data-sentry-element="TextField" data-sentry-source-file="blueprint.tsx" />

              <Button variant="solid" color="add" isDisabled={isSaving || !ruleId} onClick={handleAddStep} data-sentry-element="Button" data-sentry-source-file="blueprint.tsx">
                <FontAwesomeIcon icon={faPlus} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="blueprint.tsx" />
                Add rule
              </Button>
            </StyledFormGroup>
          </StyledRow>
        </StyledForm>
      </StyledHeader>

      {(isLoadingRules || isLoadingTasks) && <StyledLoadingSpinner icon={faSpinner} />}

      {roomId && fields.length <= 0 && <StyledEmptyDataLabel>Empty Blueprint</StyledEmptyDataLabel>}

      <FormProvider {...methods} data-sentry-element="FormProvider" data-sentry-source-file="blueprint.tsx">
        {fields.map((field, index) => <BlueprintStep
      // eslint-disable-next-line react/no-array-index-key
      key={`step-${index}-${field.ruleId}`} step={field} tasks={taskOptions} index={index} handleRemove={() => handleRemoveStep(index)} />)}

        {(fields.length || !!blueprint) && <StyledRow>
            <Button variant="solid" color="primary" isDisabled={isDisabled}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onClick={handleSubmit(onSubmit)}>
              Save changes
            </Button>
          </StyledRow>}
      </FormProvider>
    </StyledContainer>;
};